import { useState, useEffect } from "react";
import { Search, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useUserManagement } from "@/hooks/useUserManagement";
import { UserDetails, UserSummary } from "@/types/userDetails";
import { userLabels, commonPaginationLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

interface OwnerSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectOwner: (owner: UserSummary) => void;
}

// Helper function to get user type label and badge styling
const getUserTypeBadge = (role: string) => {
  const labels = userLabels(getLanguage());
  const userTypesAlternative = labels.userTypesAlternative || {};

  switch (role) {
    case "individual":
      return {
        label: userTypesAlternative.individual,
        className:
          "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/80 dark:text-blue-300 dark:border-blue-700",
      };
    case "dealer":
      return {
        label: userTypesAlternative.dealer,
        className:
          "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/80 dark:text-purple-300 dark:border-purple-700",
      };
    case "business":
      return {
        label: userTypesAlternative.business,
        className:
          "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/80 dark:text-green-300 dark:border-green-700",
      };
    default:
      return {
        label: role,
        className:
          "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/80 dark:text-gray-300 dark:border-gray-700",
      };
  }
};

export const OwnerSearchDialog = ({
  open,
  onOpenChange,
  onSelectOwner,
}: OwnerSearchDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  const limit = 10;
  const { isLoadingUsers, users, totalPages, totalCount } = useUserManagement(
    undefined,
    currentPage,
    limit,
    activeSearchQuery || undefined // Pass undefined when empty to show all users
  );

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectOwner = (owner: UserSummary) => {
    onSelectOwner(owner);
    onOpenChange(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Reset when dialog opens and load first page
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setActiveSearchQuery("");
      setCurrentPage(1);
    }
  }, [open]);

  const userTypeBadge = (role: string) => {
    const badge = getUserTypeBadge(role);
    return (
      <Badge variant="outline" className={`${badge.className} text-xs`}>
        {badge.label}
      </Badge>
    );
  };

  const labels = userLabels(getLanguage());
  const ownerSearchLabels = labels.ownerSearch || {};
  const paginationLabels = commonPaginationLabels(getLanguage());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{ownerSearchLabels.title}</DialogTitle>
          <DialogDescription>
            {activeSearchQuery
              ? ownerSearchLabels.showingResults
                  ?.replace("{count}", users.length.toString())
                  .replace("{total}", totalCount.toString())
              : ownerSearchLabels.showingAllUsers?.replace(
                  "{total}",
                  totalCount.toString()
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search input */}
          <div className="flex gap-2">
            <Input
              placeholder={ownerSearchLabels.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={isLoadingUsers}>
              {isLoadingUsers ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-auto">
            {isLoadingUsers ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : users.length > 0 ? (
              <div className="space-y-2">
                {users.map((user) => {
                  const badge = getUserTypeBadge(user.role);
                  return (
                    <div
                      key={user._id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleSelectOwner(user)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <User className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">
                              {`${user.firstName} ${user.lastName}`}
                            </span>
                            {userTypeBadge(user.role)}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectOwner(user);
                        }}
                      >
                        {ownerSearchLabels.select}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {activeSearchQuery
                  ? ownerSearchLabels.noOwnersFound
                  : ownerSearchLabels.noUsersFound}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoadingUsers}
              >
                {ownerSearchLabels.previous}
              </Button>
              <span className="flex items-center px-3 text-sm text-muted-foreground">
                {paginationLabels.pageOf
                  ?.replace("{currentPage}", currentPage.toString())
                  .replace("{totalPages}", totalPages.toString())}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoadingUsers}
              >
                {ownerSearchLabels.next}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
